import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbEdit } from "react-icons/tb";
import {
  isBaasHmlInstance,
  isBaasInstanceReleased,
  isBaasProdAwaitingLiberation,
} from "../../../../../Services/baasInstancesApi";
import { BaasInstanceEnvironmentBadge } from "./BaasInstanceEnvironmentBadge";

const ENV_PRODUCTION = "production";
const ENV_DEVELOPMENT = "development";

const getReleaseFooterLabel = (instance) => {
  if (!instance) {
    return intl.get("baas.instances.releaseStatus.envUnavailable");
  }

  if (instance?.baasInstanceId) {
    if (isBaasInstanceReleased(instance)) {
      return intl.get("baas.instances.releaseStatus.released");
    }
    if (isBaasHmlInstance(instance)) {
      return intl.get("baas.instances.environment.development");
    }
    const status = String(instance.status || "").trim();
    if (status) return status;
    return intl.get("baas.instances.releaseStatus.notReleased");
  }

  if (isBaasInstanceReleased(instance)) {
    return intl.get("baas.instances.releaseStatus.released");
  }

  const kybStatus = instance.kybStatus || "pending";

  if (kybStatus === "rejected") {
    return intl.get("baas.instances.releaseStatus.notReleased");
  }

  return intl.get("baas.instances.releaseStatus.awaitingRelease");
};

const EnvMiniCard = ({
  instance,
  environment,
  initials,
  dimmed = false,
  onSelect,
  /** Valut matrix / canUseViewAs — may open Prod while awaiting liberação. */
  canOpenAwaitingProd = false,
}) => {
  if (!instance) return null;

  const isReleased = isBaasInstanceReleased(instance);
  const isHml = environment === ENV_DEVELOPMENT || isBaasHmlInstance(instance);
  const awaitingProdLiberation = isBaasProdAwaitingLiberation(instance);
  // Partners: fully blocked. Valut matrix: same blocked visual, still clickable to open.
  const blockedForPartner = awaitingProdLiberation && !canOpenAwaitingProd;
  const showClientBlockedVisual = awaitingProdLiberation;
  const title = isHml
    ? intl.get("baas.instances.environment.hmlBadge")
    : intl.get(`baas.instances.environment.${environment}`);
  const blockedReason = intl.get("baas.instances.prodAwaitingLiberation");
  const asClientSees = intl.get("baas.instances.prodBlockedAsClientSees");
  const programCode = String(instance?.programId || "").trim();
  const isDisabled = dimmed || blockedForPartner;
  const accessibleTitle = blockedForPartner
    ? blockedReason
    : showClientBlockedVisual
      ? `${title} — ${blockedReason}`
      : title;

  return (
    <button
      type="button"
      onClick={() => {
        if (isDisabled) return;
        onSelect?.(instance);
      }}
      disabled={isDisabled}
      title={accessibleTitle}
      aria-label={accessibleTitle}
      aria-disabled={isDisabled}
      className={`relative flex min-h-[168px] min-w-[140px] flex-1 flex-col overflow-hidden rounded-xl border bg-white text-left transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-300 dark:bg-brand-dark-surface dark:focus-visible:ring-gray-600 ${
        dimmed
          ? "cursor-not-allowed border-gray-100 opacity-40 dark:border-brand-dark-border"
          : showClientBlockedVisual
            ? blockedForPartner
              ? "cursor-not-allowed border-amber-200/80 bg-amber-50/30 opacity-90 dark:border-amber-900/40 dark:bg-amber-950/10"
              : "cursor-pointer border-amber-200/80 bg-amber-50/30 hover:border-amber-300 hover:shadow-sm dark:border-amber-900/40 dark:bg-amber-950/10 dark:hover:border-amber-700"
            : "border-gray-200 hover:border-gray-300 hover:shadow-sm dark:border-brand-dark-border dark:hover:border-gray-600"
      }`}
    >
      {showClientBlockedVisual && canOpenAwaitingProd ? (
        <span className="absolute right-2 top-2 z-[1] inline-flex max-w-[calc(100%-1rem)] truncate rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">
          {asClientSees}
        </span>
      ) : null}

      <div className="flex flex-1 flex-col px-3.5 pb-2.5 pt-3.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-500 dark:bg-brand-dark-elevated dark:text-gray-400">
          {initials}
        </div>

        <p className="mt-2.5 truncate text-sm font-bold leading-tight text-brand-dark dark:text-gray-100">
          {title}
        </p>

        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <BaasInstanceEnvironmentBadge
            environment={environment}
            variant="minimal"
            className="px-2 py-px text-[10px]"
          />
        </div>
        {programCode ? (
          <p className="mt-1 truncate text-[10px] text-gray-400 dark:text-gray-500">
            {programCode}
          </p>
        ) : null}
      </div>

      <div className="border-t border-gray-100 bg-gray-50 px-3.5 py-2 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50">
        {showClientBlockedVisual ? (
          <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-[11px] font-semibold text-amber-800 dark:text-amber-200">
            {blockedReason}
          </span>
        ) : (
          <span
            className={`inline-flex min-w-0 items-center gap-1.5 truncate text-[11px] ${
              isReleased
                ? "text-gray-400 dark:text-gray-500"
                : "font-medium text-gray-500 dark:text-gray-400"
            }`}
          >
            {isReleased ? (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                aria-hidden
              />
            ) : null}
            {getReleaseFooterLabel(instance)}
          </span>
        )}
      </div>
    </button>
  );
};

const CreateHmlCard = ({ onCreate, busy }) => (
  <div className="flex min-h-[168px] min-w-[140px] flex-1 flex-col justify-between overflow-hidden rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-3.5 dark:border-amber-700/60 dark:bg-amber-950/20">
    <div>
      <p className="text-sm font-bold text-brand-dark dark:text-gray-100">
        {intl.get("baas.instances.createHmlCard.title")}
      </p>
      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
        {intl.get("baas.instances.createHmlCard.hint")}
      </p>
    </div>
    <button
      type="button"
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation();
        onCreate?.();
      }}
      className="mt-3 inline-flex h-9 items-center justify-center rounded-lg bg-[#e7a73f] px-3 text-sm font-semibold text-white transition hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy
        ? intl.get("baas.instances.createHmlCard.creating")
        : intl.get("baas.instances.createHmlCard.action")}
    </button>
  </div>
);

/**
 * Program group: light outer shell with program name + two clickable env mini-cards
 * (Production / HML) side by side — same look as the previous standalone instance cards.
 * Page filter Todos | HML | Production shows only the matching inner card.
 * When prod exists without linked HML, shows Criar HML card (cross-DB twin in bank).
 */
export const BaasInstanceCard = ({
  group,
  /** @deprecated Prefer `group` — kept for single-instance callers. */
  instance,
  preferredEnv = "all",
  onSelect,
  onEdit,
  onCreateHml,
  instances = [],
  /** Valut matrix (canUseViewAs / raw admin) may open Prod awaiting liberação. */
  canOpenAwaitingProd = false,
}) => {
  const [creatingHml, setCreatingHml] = useState(false);

  const programGroup = useMemo(() => {
    if (group) return group;
    if (!instance) return null;
    const isHml = isBaasHmlInstance(instance);
    return {
      id: instance.id,
      programId: instance.programId,
      name: instance.displayName || instance.name,
      initials: instance.initials,
      production: isHml ? null : instance,
      development: isHml ? instance : null,
    };
  }, [group, instance]);

  if (!programGroup) return null;

  const production = programGroup.production ?? null;
  const development = programGroup.development ?? null;
  const missingHml =
    Boolean(production?.baasInstanceId) &&
    !development &&
    !(Number(production?.hmlInstanceId) > 0);

  const showProduction =
    Boolean(production) &&
    (preferredEnv === "all" || preferredEnv === "production");
  const showDevelopment =
    Boolean(development) &&
    (preferredEnv === "all" || preferredEnv === "hml");
  const showCreateHml =
    missingHml &&
    typeof onCreateHml === "function" &&
    (preferredEnv === "all" || preferredEnv === "hml" || preferredEnv === "production");

  const lanes = [];
  if (showProduction) {
    lanes.push({
      key: ENV_PRODUCTION,
      instance: production,
      environment: ENV_PRODUCTION,
    });
  }
  if (showDevelopment) {
    lanes.push({
      key: ENV_DEVELOPMENT,
      instance: development,
      environment: ENV_DEVELOPMENT,
    });
  }

  // Safety: if filter hid both but the group still rendered, show whatever exists.
  if (lanes.length === 0 && !showCreateHml) {
    if (production) {
      lanes.push({
        key: ENV_PRODUCTION,
        instance: production,
        environment: ENV_PRODUCTION,
      });
    } else if (development) {
      lanes.push({
        key: ENV_DEVELOPMENT,
        instance: development,
        environment: ENV_DEVELOPMENT,
      });
    }
  }

  const editTarget =
    onEdit &&
    [production, development].find((row) => row && !row.baasInstanceId);

  const handleCreateHml = async () => {
    if (!production || !onCreateHml) return;
    setCreatingHml(true);
    try {
      await onCreateHml(production);
    } finally {
      setCreatingHml(false);
    }
  };

  return (
    <div
      className="group relative flex h-full min-w-0 w-full flex-col self-stretch overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-50/60 p-3 transition duration-200 dark:border-brand-dark-border dark:bg-brand-dark-elevated/30"
      role="group"
      aria-label={programGroup.name}
    >
      {editTarget ? (
        <button
          type="button"
          className="btn-outline-icon absolute right-2.5 top-2.5 z-10 bg-white/90 opacity-0 shadow-sm backdrop-blur-sm transition group-hover:opacity-100 focus:opacity-100 dark:bg-brand-dark-surface/90"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(editTarget);
          }}
          aria-label={intl.get("baas.instances.edit", { name: programGroup.name })}
        >
          <TbEdit className="h-4 w-4" />
        </button>
      ) : null}

      <div className="mb-2.5 flex items-center gap-2 px-1 pr-10">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-semibold text-gray-500 shadow-sm dark:bg-brand-dark-surface dark:text-gray-400">
          {programGroup.initials}
        </div>
        <p className="truncate text-sm font-bold leading-tight text-brand-dark dark:text-gray-100">
          {programGroup.name}
        </p>
      </div>

      <div
        className={`flex gap-2.5 ${
          lanes.length + (showCreateHml ? 1 : 0) > 1 ? "flex-col sm:flex-row" : "flex-row"
        }`}
      >
        {lanes.map((lane) => (
          <EnvMiniCard
            key={lane.key}
            instance={lane.instance}
            environment={lane.environment}
            initials={programGroup.initials}
            onSelect={onSelect}
            canOpenAwaitingProd={canOpenAwaitingProd}
          />
        ))}
        {showCreateHml ? (
          <CreateHmlCard onCreate={handleCreateHml} busy={creatingHml} />
        ) : null}
      </div>
    </div>
  );
};
